import { useEffect, useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import AuthHexBackground from './AuthHexBackground';
import PasswordField from './PasswordField';
import './Auth.css';

function OwnerLogin() {
  const { login, loading, isAuthenticated, account, authNotice, clearAuthNotice } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [form, setForm] = useState({ email: '', password: '' });
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const from = location.state?.from;

  useEffect(() => {
    if (authNotice) setError(authNotice);
    else if (location.state?.notice) setError(location.state.notice);
  }, [authNotice, location.state?.notice]);

  // Only auto-redirect owners, and never while a login attempt is in progress
  if (!loading && !submitting && isAuthenticated && account?.role === 'owner') {
    return <Navigate to={from && String(from).startsWith('/owner') ? from : '/owner/dashboard'} replace />;
  }

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    setError('');
    clearAuthNotice();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');

    try {
      await login(form.email, form.password, { allowedRoles: ['owner'] });
      const target = from && String(from).startsWith('/owner') ? from : '/owner/dashboard';
      navigate(target, { replace: true });
    } catch (err) {
      setError(err.message || 'Login failed. Check your email and password.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="auth-loading">
        <AuthHexBackground />
        <div className="auth-loading-spinner" aria-hidden="true" />
        <p>Loading…</p>
      </div>
    );
  }

  return (
    <div className="auth-page">
      <AuthHexBackground />
      <div className="auth-card auth-card-neon">
        <div className="auth-brand">
          <h1>Owner Panel</h1>
          <p>Sign in to manage the Gym CRM platform</p>
        </div>

        {error && <p className="auth-error" role="alert">{error}</p>}

        <form className="auth-form" onSubmit={handleSubmit}>
          <div className="owner-form-group">
            <label className="owner-form-label" htmlFor="owner-login-email">Email</label>
            <input
              id="owner-login-email"
              name="email"
              type="email"
              className="owner-form-input"
              value={form.email}
              onChange={handleChange}
              autoComplete="email"
              required
            />
          </div>
          <PasswordField
            id="owner-login-password"
            name="password"
            value={form.password}
            onChange={handleChange}
            autoComplete="current-password"
            required
          />
          <button type="submit" className="auth-submit" disabled={submitting}>
            {submitting ? 'Signing in…' : 'Sign In as Owner'}
          </button>
        </form>
      </div>
    </div>
  );
}

export default OwnerLogin;
